'use client';

import type { ChatAuthor, ChatRefDoc } from '@/app/services/chat';
import { MarkdownRenderer } from '@/components/markdown-renderer';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { type Components } from 'react-markdown';

interface Props {
  content: string;
  refs: ChatRefDoc[];
  onDetail: (index: number) => void;
  className?: string;
}

/**
 * 预处理引用标记：把上游的 `[[n]]` / `[[n]][[m]]` 以及翻译结果里的 `[n]`
 * 变成内联 <cite> 元素。
 * rehype-raw 会原样保留，再由 markdownComponents.cite 渲染成 <CiteTag>。
 * 非法编号 (对应 ref 不存在) 直接丢弃，不渲染为气泡。
 */
function preprocessCitations(md: string, refCount: number): string {
  const replaceCitation = (_match: string, n: string) => {
    const idx = Number(n) - 1;
    if (!Number.isFinite(idx) || idx < 0 || idx >= refCount) {
      return '';
    }
    return `<cite data-ref-idx="${idx}">${n}</cite>`;
  };

  return md
    .replace(/\[\[(\d+)\]\]/g, replaceCitation)
    .replace(/(?<!\[)\[(\d+)\](?!\]|\()/g, replaceCitation);
}

function decodeXmlEntities(value: string) {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function extractMathMlText(value: string) {
  const text = value
    .replace(/<mml:mo[^>]*>\s*<\/mml:mo>/gi, ' ')
    .replace(/<mml:mo[^>]*>(.*?)<\/mml:mo>/gi, '$1')
    .replace(/<mml:mtext[^>]*>(.*?)<\/mml:mtext>/gi, '$1')
    .replace(/<mml:mi[^>]*>(.*?)<\/mml:mi>/gi, '$1')
    .replace(/<mml:mn[^>]*>(.*?)<\/mml:mn>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return decodeXmlEntities(text);
}

function unwrapEnsureMath(value: string) {
  let next = value;
  for (let i = 0; i < 4; i += 1) {
    const unwrapped = next.replace(/\\ensuremath\{([^{}]*)\}/g, '$1');
    if (unwrapped === next) return next;
    next = unwrapped;
  }
  return next;
}

function normalizeTexConditionals(value: string) {
  return value
    .replace(
      /\\ifmmode\s*\^?\\circ\s*\\else\s*\\textdegree\s*\\fi(?:\{\})?/g,
      '\\circ',
    )
    .replace(
      /\\ifmmode\s*\\?pm\s*\\else\s*\\textpm\s*\\fi(?:\{\})?/g,
      '\\pm',
    )
    .replace(
      /\\ifmmode\s*([\s\S]*?)\s*\\else\s*[\s\S]*?\s*\\fi(?:\{\})?/g,
      (_match, mathBranch: string) => mathBranch.trim(),
    );
}

function preprocessMath(md: string) {
  return normalizeTexConditionals(
    unwrapEnsureMath(
      md.replace(/<mml:math\b[\s\S]*?<\/mml:math>/gi, (match) => {
        const text = extractMathMlText(match);
        return text ? `$${text}$` : '';
      }),
    ),
  );
}

function abbreviateAuthors(authors: ChatAuthor[] | null | undefined): string {
  if (!authors || authors.length === 0) return '';
  const heads = authors.slice(0, 2).map((a) => {
    const directName = a.name?.trim();
    if (directName) return directName;
    const fn = a.fore_name?.[0] ?? '';
    return `${a.last_name ?? ''}${fn ? ' ' + fn : ''}`.trim();
  });
  const tail = authors.length > 2 ? ' 等' : '';
  return heads.filter(Boolean).join(', ') + tail;
}

function CiteTag({
  idx,
  num,
  refs,
  onDetail,
}: {
  idx: number;
  num: string;
  refs: ChatRefDoc[];
  onDetail: (index: number) => void;
}) {
  const ref = refs[idx];
  if (!ref) return null;

  const authorsLine = abbreviateAuthors(ref.authors);
  const yearLine = ref.pub_year ? String(ref.pub_year) : '';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onDetail(idx)}
          className={cn(
            'inline-flex items-center justify-center align-middle',
            'min-w-4 h-4 px-1 mx-0.5 mb-0.5 rounded-full',
            'text-[10px] leading-none font-medium',
            'bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer',
          )}
        >
          {num}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p className="text-xs font-medium line-clamp-2">{ref.title}</p>
        {(authorsLine || yearLine) && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {[authorsLine, yearLine].filter(Boolean).join(' · ')}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * /search 页面聊天答案的 markdown 渲染器。用 react-markdown + remark-gfm + rehype-raw，
 * 所有节点都走 React 组件（不用 dangerouslySetInnerHTML），样式贴合 app 的 Tailwind + 主题 token。
 * 引用标记以 <cite> 的形式传下来，最终渲染为点击跳 onDetail + hover 显示文献简介的角标。
 */
export function MessageMarkdown({ content, refs, onDetail, className }: Props) {
  const processed = preprocessCitations(preprocessMath(content), refs.length);

  const components: Components = {
    p: ({ children, ...props }) => (
      <p
        className="my-3 text-[15px] leading-7 text-foreground/92 md:text-base"
        {...props}
      >
        {children}
      </p>
    ),
    ul: ({ ...props }) => (
      <ul
        className="my-3 list-disc space-y-2 pl-6 text-foreground/92"
        {...props}
      />
    ),
    ol: ({ ...props }) => (
      <ol
        className="my-3 list-decimal space-y-2 pl-6 text-foreground/92"
        {...props}
      />
    ),
    li: ({ ...props }) => <li className="leading-8" {...props} />,
    // react-markdown 不认识 cite，但 rehype-raw 把它当 inline HTML 处理。
    // 这里强类型 cast 以提取 data-ref-idx。
    cite: ({ children, ...rest }) => {
      const idxStr = (rest as Record<string, string>)['data-ref-idx'];
      const idx = Number(idxStr);
      if (!Number.isFinite(idx)) return <>{children}</>;
      return (
        <CiteTag
          idx={idx}
          num={String(children)}
          refs={refs}
          onDetail={onDetail}
        />
      );
    },
  };

  return (
    <MarkdownRenderer
      content={processed}
      className={cn('text-base text-foreground', className)}
      allowHtml
      enableMath
      compact
      components={components}
    />
  );
}
